import * as React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ThemeProvider, createTheme } from '@mui/material';
import { Button, Spinner } from '@/components/Elements';
import { Notifications } from '@/components/Notifications/Notifications';
import { AuthProvider } from '@/lib/auth';
import { queryClient } from '@/lib/react-query';
import { useNavigate } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { PostHogProvider } from 'posthog-js/react';
import {
  GOOGLE_CLIENT_ID,
  PUBLIC_POSTHOG_HOST,
  PUBLIC_POSTHOG_KEY,
} from '@/config';
import { GoogleOAuthProvider } from '@react-oauth/google';
import 'primereact/resources/themes/tailwind-light/theme.css';
import { ErrorBoundary } from '@sentry/react';
import useIframe from '@/hooks/useIframe';

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins',
  },
});

const options = {
  api_host: PUBLIC_POSTHOG_HOST,
};

const ErrorFallback = () => {
  const { isAppInIframe } = useIframe();
  return (
    <div
      className="w-screen h-screen flex flex-col justify-center items-center"
      role="alert"
    >
      <h2 className="text-3xl font-medium tracking-wide">
        Oops! Something went wrong.
      </h2>
      <h3 className="text-xl text-brand-ai-gray-300 mt-2">
        Please try refreshing the page or try again later
      </h3>
      {!isAppInIframe && (
        <Button
          className="mt-10 rounded-xl"
          onClick={() => window.location.assign(window.location.origin)}
        >
          Go Back to Homepage
        </Button>
      )}
    </div>
  );
};

export const AppProvider = ({ children }) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleUnauthorized = (event) => {
      const { navigateTo } = event.detail;
      navigate(navigateTo);
    };

    window.addEventListener('logout', handleUnauthorized);

    return () => {
      window.removeEventListener('logout', handleUnauthorized);
    };
  }, [navigate]);

  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center w-screen h-screen">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary fallback={ErrorFallback}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <PrimeReactProvider>
              <ThemeProvider theme={theme}>
                {/* {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />} */}
                <Notifications />
                <PostHogProvider apiKey={PUBLIC_POSTHOG_KEY} options={options}>
                  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                    <AuthProvider>{children}</AuthProvider>
                  </GoogleOAuthProvider>
                </PostHogProvider>
              </ThemeProvider>
            </PrimeReactProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
